type RouteState = { path: string; search: string };

const isBrowser = typeof window !== "undefined" && typeof document !== "undefined";

class Router {
  state = $state<RouteState>({
    path: isBrowser ? window.location.pathname : "/",
    search: isBrowser ? window.location.search : "",
  });

  constructor() {
    if (!isBrowser) return;

    window.addEventListener("popstate", () => this.sync());
    document.addEventListener("click", (event: MouseEvent) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;
      const target = event.target instanceof Element ? event.target : null;
      const anchor = target?.closest<HTMLAnchorElement>("a[data-spa]") ?? null;
      if (!anchor || anchor.target || anchor.hasAttribute("download") || event.defaultPrevented)
        return;
      if (anchor.origin !== window.location.origin) return;
      event.preventDefault();
      this.navigate(anchor.pathname + anchor.search + anchor.hash);
    });
  }

  private sync() {
    if (!isBrowser) return;
    this.state = { path: window.location.pathname, search: window.location.search };
    if (window.location.hash) {
      requestAnimationFrame(() =>
        document.querySelector(window.location.hash)?.scrollIntoView({ behavior: "smooth" }),
      );
    } else {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
    this.focusMain();
  }

  /**
   * The SPA never reloads, so without this a keyboard user's focus stays on the
   * link they just activated while the whole page changes underneath them.
   */
  private focusMain() {
    const main = document.querySelector<HTMLElement>("main");
    if (!main) return;
    if (!main.hasAttribute("tabindex")) main.setAttribute("tabindex", "-1");
    requestAnimationFrame(() => main.focus({ preventScroll: true }));
  }

  navigate(href: string, replace = false) {
    if (!isBrowser) return;
    if (replace) window.history.replaceState({}, "", href);
    else window.history.pushState({}, "", href);
    this.sync();
  }
}

export const router = new Router();
